import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, RefreshCcw } from 'lucide-react';

interface Transaction {
    id: number;
    amount: number;
    status: number;
    type: number;
    balanceAfter: number;
    createdAt: string;
}

export default function TransactionHistory() {

    const [isLoading, setIsLoading] = useState(false);
    const [transactionData, setTransactionData] = useState<Transaction[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => { getTransactionHistoryData(); }, [])
    useEffect(() => { getTransactionHistoryData(); }, [currentPage])

    const getTransactionHistoryData = async () => {
        setIsLoading(true);
        try {
            const response = await fetch("https://user-api.u-slot.vip/api/Alarm/get_chargedischarge_list", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    page: currentPage,
                    sortBy: 'createdAt',
                    sortOrder: 'DESC',
                    pageSize: 6
                }),
            });

            if (response.ok) {
                const data = await response.json();
                setCurrentPage(data.data.page)
                setTransactionData(data.data.items);
                setTotalPages(data.data.totalPages);
                setTotalCount(data.data.totalCount);
            } else {
                console.log('Error fetching charge data:', 'error');
            }
        } catch (error) {
            console.log('Error fetching charge data:', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const getPaginationRange = () => {
        const range = [];
        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);

        if (end - start + 1 < maxVisible) {
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            range.push(i);
        }
        return range;
    };

    const getStatusColor = (status: number) => {
        let colorClassName;
        if (status === 0) {
            colorClassName = 'bg-green-500/20 text-green-400';
        } else if (status === 1) {
            colorClassName = 'bg-yellow-500/20 text-yellow-400';
        } else if (status === 2) {
            colorClassName = 'bg-cyan-500/20 text-cyan-400';
        } else {
            colorClassName = 'bg-red-500/20 text-red-400';
        }

        return colorClassName;
    };

    const getStatusText = (status: number) => {
        let statusStr = "unkonwn";
        if (status === 0) {
            statusStr = "Success";
        } else if (status === 1) {
            statusStr = "Pending";
        } else if (status === 2) {
            statusStr = "Progress";
        } else if (status === 3) {
            statusStr = "Failed";
        }

        return statusStr;
    };

    const formatDateTime = (dateString: string): string => {
        const date = new Date(dateString);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const refreshData = () => {
        getTransactionHistoryData()
    }

    return (
        <div>
            <div className="bg-[#1a1c24] border border-[#FFD349]/20 rounded-xl p-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                        Recent Transactions
                    </h2>
                    <RefreshCcw onClick={refreshData} size={20} className="text-white mb-6 cursor-pointer" />
                </div>
                {isLoading ? (<div className="flex items-center justify-center py-12">
                    <div className="w-12 h-12 border-4 border-gray-300 border-t-white rounded-full animate-spin"></div>
                </div>) : (
                    <div className="space-y-4">
                        {transactionData.map((tx) => (
                            <div
                                key={tx.id}
                                className="p-4 bg-[#0D0E11] border border-[#FFD349]/10 rounded-lg hover:border-[#FFD349]/30 transition-all"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tx.type === 1
                                            ? 'bg-gradient-to-r from-blue-400/20 to-blue-500/20 border border-blue-400/30'
                                            : 'bg-gradient-to-r from-green-400/20 to-green-500/20 border border-green-400/30'
                                            }`}>
                                            <i className={`${tx.type === 1 ? 'ri-arrow-up-line text-blue-400' : 'ri-arrow-down-line text-green-400'
                                                } text-lg`}></i>
                                        </div>
                                        <div>
                                            <p className="text-white font-semibold text-sm whitespace-nowrap" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                                {tx.type === 1 ? 'Deposit' : 'Withdrawal'}
                                            </p>
                                            <p className="text-white/60 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}></p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className={`font-bold text-sm ${tx.type === 1 ? 'text-blue-400' : 'text-green-400'
                                            }`} style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                            {tx.type === 1 ? '+' : '-'}${tx.amount.toLocaleString()}
                                        </p>
                                        <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${getStatusColor(tx.status)
                                            }`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                            {getStatusText(tx.status)}
                                        </span>
                                    </div>
                                </div>
                                <p className="text-white/60 text-xs font-mono truncate">
                                    {formatDateTime(tx.createdAt)}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="pagination-section bg-\[\#1a1c24\]">
                        <div className="pagination-controls">
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                className="pagination-btn"
                            >
                                <ChevronsLeft size={20} />
                            </button>

                            <button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="pagination-btn"
                            >
                                <ChevronLeft size={20} />
                            </button>

                            {getPaginationRange().map((page) => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`pagination-number ${page === currentPage ? 'active' : ''}`}
                                >
                                    {page}
                                </button>
                            ))}

                            <button
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="pagination-btn"
                            >
                                <ChevronRight size={20} />
                            </button>

                            <button
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages}
                                className="pagination-btn"
                            >
                                <ChevronsRight size={20} />
                            </button>
                        </div>
                    </div>)
                }
                {(transactionData.length == 0 && !isLoading) && (<p className="text-lg md:text-2xl mb-2 md:mb-3 drop-shadow-lg text-white/60 text-center font-medium">Empty Data</p>)}
            </div>
            <style>{`
                /* Pagination Section */
                .pagination-section {
                    padding-top: 1rem;
                    padding-bottom: 1rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 1rem;
                }

                .pagination-info {
                    font-size: 0.875rem;
                    color: #4b5563;
                }

                .pagination-controls {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }

                .pagination-btn {
                    padding: 0.5rem;
                    border-radius: 0.5rem;
                    border: none;
                    background-color: transparent;
                    cursor: pointer;
                    transition: background-color 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .pagination-btn:hover:not(:disabled) {
                    background-color: white;
                }

                .pagination-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .pagination-btn svg {
                    width: 20px;
                    height: 20px;
                    color: #FFC107 var(--tw-gradient-to-position);
                }

                .pagination-number {
                    min-width: 40px;
                    padding-left: 0.75rem;
                    padding-right: 0.75rem;
                    padding-top: 0.5rem;
                    padding-bottom: 0.5rem;
                    border-radius: 0.5rem;
                    border: none;
                    background-color: transparent;
                    color: #FFC107 var(--tw-gradient-to-position);
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .pagination-number:hover {
                    background-color: white;
                }

                .pagination-number.active {
                    background-color: #FFC107 var(--tw-gradient-to-position);
                    color: black;
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
                }
            `}</style>
        </div>
    )
}